import React, { createContext, useContext, useRef, useState, useEffect } from "react";
import { api, fileStreamUrl } from "../lib/api";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [queue, setQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState("off"); // off | one | all
  const [showQueue, setShowQueue] = useState(false);

  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  const playTrack = async (track, list = []) => {
    setCurrentTrack(track);
    if (list && list.length) setQueue(list);
    setIsPlaying(true);
    try { await api.post(`/tracks/${track.track_id}/play`); } catch (e) {}
    try { await api.post(`/history`, { track_id: track.track_id }); } catch (e) {}
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (audio.paused) { audio.play(); setIsPlaying(true); }
    else { audio.pause(); setIsPlaying(false); }
  };

  const nextIndex = () => {
    if (!queue.length) return -1;
    const idx = queue.findIndex((t) => t.track_id === currentTrack?.track_id);
    if (shuffle) {
      if (queue.length < 2) return idx;
      let n = idx;
      while (n === idx) n = Math.floor(Math.random() * queue.length);
      return n;
    }
    return (idx + 1) % queue.length;
  };

  const next = () => {
    if (!currentTrack || !queue.length) return;
    const n = nextIndex();
    if (n >= 0 && queue[n]) playTrack(queue[n], queue);
  };

  const prev = () => {
    if (!currentTrack || !queue.length) return;
    const idx = queue.findIndex((t) => t.track_id === currentTrack.track_id);
    const prevTrack = queue[(idx - 1 + queue.length) % queue.length];
    if (prevTrack) playTrack(prevTrack, queue);
  };

  const seek = (pct) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = pct * duration;
  };

  const toggleShuffle = () => setShuffle(!shuffle);
  const toggleRepeat = () => setRepeat(repeat === "off" ? "all" : repeat === "all" ? "one" : "off");
  const addToQueue = (track) => setQueue((q) => [...q, track]);
  const removeFromQueue = (trackId) => setQueue((q) => q.filter((t) => t.track_id !== trackId));
  const clearQueue = () => setQueue([]);

  const onEnded = () => {
    if (repeat === "one") {
      const audio = audioRef.current;
      if (audio) { audio.currentTime = 0; audio.play(); }
      return;
    }
    if (repeat === "all" || queue.length > 1) {
      next();
    } else {
      setIsPlaying(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.code === "Space") { e.preventDefault(); togglePlay(); }
      if (e.code === "ArrowRight" && e.shiftKey) { e.preventDefault(); next(); }
      if (e.code === "ArrowLeft" && e.shiftKey) { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [currentTrack, queue, isPlaying]);

  return (
    <PlayerContext.Provider
      value={{
        audioRef, currentTrack, isPlaying, progress, duration, volume, queue,
        shuffle, repeat, showQueue,
        setVolume, setShowQueue,
        playTrack, togglePlay, next, prev, seek,
        toggleShuffle, toggleRepeat, addToQueue, removeFromQueue, clearQueue,
      }}
    >
      {currentTrack && currentTrack.track_type === "audio" && (
        <audio
          ref={audioRef}
          src={fileStreamUrl(currentTrack.file_path)}
          autoPlay
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onEnded={onEnded}
        />
      )}
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
